import React, { useEffect, useState } from 'react';
import { getApiUrl } from '../utils/apiConfig';
import { db, auth } from '../services/firebase';
import { collection, addDoc, query, where, orderBy, serverTimestamp, doc, getDoc, onSnapshot, deleteDoc, updateDoc } from 'firebase/firestore';
import { getStorage, ref, uploadBytes, getDownloadURL, deleteObject } from 'firebase/storage';
import { Typography, TextField, Button, Card, CardContent, List, Box, ListItem, ListItemText, IconButton, CircularProgress, Select, MenuItem, FormControl, InputLabel, Link as MuiLink, Checkbox, FormControlLabel } from '@mui/material';
import Grid from '@mui/material/Grid';
import { DatePicker } from '@mui/x-date-pickers/DatePicker';
import DeleteIcon from '@mui/icons-material/Delete';
import styles from '../styles/Dashboard.module.css';

const Documentos = () => {
  const [documentos, setDocumentos] = useState([]);
  const [novoDocumento, setNovoDocumento] = useState({ nome: '', categoria: '', estoque_id: '' });
  const [estoqueItems, setEstoqueItems] = useState([]);
  const [arquivo, setArquivo] = useState(null);
  const [dataVencimento, setDataVencimento] = useState(null);
  const [condominioId, setCondominioId] = useState('');
  const [userRole, setUserRole] = useState('');
  const [uploading, setUploading] = useState(false);
  const [isPublico, setIsPublico] = useState(false);
  const [filtroCategoria, setFiltroCategoria] = useState('');
  const [filtroDataVencimento, setFiltroDataVencimento] = useState(null);

  const categorias = ['Laudo de Inspeção', 'Certificado', 'ART', 'Alvará', 'Outro'];

  useEffect(() => {
    const unsubscribe = auth.onAuthStateChanged(async (user) => {
      if (user) {
        const userDocRef = doc(db, 'tb_usuarios', user.uid);
        const userDoc = await getDoc(userDocRef);
        if (userDoc.exists()) {
          const userData = userDoc.data();
          setCondominioId(userData.condominio_id);
          setUserRole(userData.papel);
        }
      }
    });
    return () => unsubscribe();
  }, []);

  const isAdmin = userRole && ['sindico', 'sub_sindico', 'conselheiro'].includes(userRole);

  // Busca itens de estoque para o dropdown (apenas se for admin)
  useEffect(() => {
    if (!condominioId || !isAdmin) return;
    const q = query(collection(db, 'tb_estoque'), where('condominio_id', '==', condominioId));
    const unsubscribe = onSnapshot(q, (snapshot) => {
      const list = snapshot.docs.map(doc => ({ id: doc.id, ...doc.data() }));
      setEstoqueItems(list);
    });
    return () => unsubscribe();
  }, [condominioId, isAdmin]);

  useEffect(() => {
    if (!condominioId || !userRole) return;

    let q;
    if (isAdmin) {
      // Administradores podem ver todos os documentos do condomínio.
      q = query(collection(db, 'tb_documentos'), where('condominio_id', '==', condominioId), orderBy('created_at', 'desc'));
    } else {
      // Moradores veem apenas os documentos públicos.
      q = query(collection(db, 'tb_documentos'), where('condominio_id', '==', condominioId), where('publico', '==', true), orderBy('created_at', 'desc'));
    }

    const unsubscribe = onSnapshot(q, (querySnapshot) => {
      const docsList = querySnapshot.docs.map(doc => ({ id: doc.id, ...doc.data() }));
      setDocumentos(docsList);
    }, (error) => {
      console.error("Erro ao buscar documentos:", error);
      // Este erro pode ocorrer se o índice do Firestore não foi criado.
    });

    return () => unsubscribe();
  }, [condominioId, userRole, isAdmin]);

  const handleCreateDocumento = async () => {
    if (!novoDocumento.nome || !novoDocumento.categoria || !arquivo) {
      alert('Por favor, preencha todos os campos e selecione um arquivo PDF.');
      return;
    }

    setUploading(true);
    try {
      // Encontra o nome do equipamento selecionado, se houver
      let estoque_nome = '';
      if (novoDocumento.estoque_id) {
        const item = estoqueItems.find(i => i.id === novoDocumento.estoque_id);
        if (item) estoque_nome = item.nome;
      }

      // 1. Cria o documento no Firestore primeiro para obter um ID
      const docRef = await addDoc(collection(db, 'tb_documentos'), {
        ...novoDocumento,
        condominio_id: condominioId,
        publico: isPublico,
        estoque_id: novoDocumento.estoque_id || null,
        estoque_nome: estoque_nome || null,
        created_at: serverTimestamp(),
        data_vencimento: dataVencimento,
        // Deixa os campos de arquivo vazios por enquanto
        url_pdf: '',
        path_pdf: '',
      });

      // 2. Usa o ID do documento para nomear o arquivo no Storage
      const fileExtension = arquivo.name.split('.').pop();
      const filePath = `documentos/${condominioId}/${docRef.id}.${fileExtension}`;
      const fileRef = ref(getStorage(), filePath);
      const metadata = {
        customMetadata: {
          'userRole': userRole,
          'condominioId': condominioId,
          'ownerId': auth.currentUser.uid
        }
      };

      await uploadBytes(fileRef, arquivo, metadata); // Envia o arquivo com os metadados
      const fileUrl = await getDownloadURL(fileRef);

      // 4. Atualiza o documento no Firestore com as URLs do arquivo
      await updateDoc(docRef, {
        url_pdf: fileUrl,
        path_pdf: filePath,
      });

      setNovoDocumento({ nome: '', categoria: '', estoque_id: '' });
      setArquivo(null);
      setDataVencimento(null);
      setIsPublico(false);
      alert('Documento adicionado com sucesso!');
    } catch (error) {
      alert('Erro ao adicionar documento: ' + error.message);
    } finally {
      setUploading(false);
    }
  };

  const handleDeleteDocumento = async (documento) => {
    if (!window.confirm(`Tem certeza que deseja deletar o documento "${documento.nome}"?`)) return;

    try {
      const token = await auth.currentUser.getIdToken();

      // ...

      // ...
      const response = await fetch(`${getApiUrl()}/api/documentos/${documento.id}`, {
        method: 'DELETE',
        headers: {
          'Authorization': `Bearer ${token}`,
        },
      });

      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.error || 'Erro ao deletar documento.');
      }

      // O onSnapshot cuidará de remover o item da lista na UI.

      alert('Documento deletado com sucesso!');
    } catch (error) {
      console.error('Erro ao deletar documento:', error);
      alert('Erro ao deletar documento: ' + error.message);
    }
  };

  const formatarData = (timestamp) => {
    if (!timestamp) return 'N/A';
    const data = timestamp.toDate();
    return new Intl.DateTimeFormat('pt-BR').format(data);
  };

  const documentosFiltrados = documentos.filter(doc => {
    const matchCategoria = filtroCategoria ? doc.categoria === filtroCategoria : true;
    const matchData = filtroDataVencimento ?
      doc.data_vencimento && doc.data_vencimento.toDate().toDateString() === filtroDataVencimento.toDateString()
      : true;

    return matchCategoria && matchData;
  });

  const limparFiltros = () => {
    setFiltroCategoria('');
    setFiltroDataVencimento(null);
  };


  return (
    <Box>
      <Box component="main" sx={{ flexGrow: 1, p: 3, background: 'linear-gradient(45deg, #283593 30%, #1976d2 90%)', minHeight: '100vh' }}>
        <Typography variant="h4" gutterBottom sx={{ color: 'white' }}>Controle de Documentos</Typography>

        {isAdmin && (
          <Card className={styles.card} sx={{ mb: 4 }}>
            <CardContent>
              <Typography variant="h5" className={styles.sectionTitle}>Adicionar Novo Documento</Typography>
              <Grid container spacing={2} alignItems="center">
                <Grid item xs={12} sm={6}>
                  <TextField fullWidth label="Nome do Documento" value={novoDocumento.nome} onChange={(e) => setNovoDocumento({ ...novoDocumento, nome: e.target.value })} />
                </Grid>
                <Grid item xs={12} sm={6}>
                  <FormControl fullWidth>
                    <InputLabel>Categoria</InputLabel>
                    <Select value={novoDocumento.categoria} label="Categoria" onChange={(e) => setNovoDocumento({ ...novoDocumento, categoria: e.target.value })}>
                      {categorias.map(cat => <MenuItem key={cat} value={cat}>{cat}</MenuItem>)}
                    </Select>
                  </FormControl>
                </Grid>
                <Grid item xs={12} sm={6}>
                  <FormControl fullWidth>
                    <InputLabel>Equipamento Associado (Opcional)</InputLabel>
                    <Select value={novoDocumento.estoque_id} label="Equipamento Associado (Opcional)" onChange={(e) => setNovoDocumento({ ...novoDocumento, estoque_id: e.target.value })}>
                      <MenuItem value=""><em>Nenhum</em></MenuItem>
                      {estoqueItems.map(item => <MenuItem key={item.id} value={item.id}>{item.nome}</MenuItem>)}
                    </Select>
                  </FormControl>
                </Grid>
                <Grid item xs={12} sm={6}>
                  <DatePicker label="Data de Vencimento (Opcional)" value={dataVencimento} onChange={(newValue) => setDataVencimento(newValue)} sx={{ width: '100%' }} />
                </Grid>
                <Grid item xs={12} sm={6}>
                  <Button variant="outlined" component="label" fullWidth>
                    Selecionar PDF
                    <input type="file" hidden accept="application/pdf" onChange={(e) => setArquivo(e.target.files[0])} />
                  </Button>
                  {arquivo && <Typography sx={{ mt: 1, fontStyle: 'italic' }}>{arquivo.name}</Typography>}
                </Grid>
                <Grid item xs={12}>
                  <FormControlLabel
                    control={<Checkbox checked={isPublico} onChange={(e) => setIsPublico(e.target.checked)} />}
                    label="Documento Público (visível para moradores)"
                  />
                </Grid>
                <Grid item xs={12}>
                  <Button variant="contained" onClick={handleCreateDocumento} disabled={uploading}>
                    {uploading ? <CircularProgress size={24} /> : 'Adicionar Documento'}
                  </Button>
                </Grid>
              </Grid>
            </CardContent>
          </Card>
        )}

        <Card className={styles.card}>
          <CardContent>
            <Typography variant="h5" className={styles.sectionTitle}>Documentos do Condomínio</Typography>

            <Grid container spacing={2} sx={{ mb: 3 }} alignItems="center">
              <Grid item xs={12} sm={5}>
                <FormControl fullWidth size="small">
                  <InputLabel>Filtrar por Categoria</InputLabel>
                  <Select value={filtroCategoria} label="Filtrar por Categoria" onChange={(e) => setFiltroCategoria(e.target.value)}>
                    <MenuItem value=""><em>Todas</em></MenuItem>
                    {categorias.map(cat => <MenuItem key={cat} value={cat}>{cat}</MenuItem>)}
                  </Select>
                </FormControl>
              </Grid>
              <Grid item xs={12} sm={5}>
                <DatePicker
                  label="Filtrar por Vencimento"
                  value={filtroDataVencimento}
                  onChange={(newValue) => setFiltroDataVencimento(newValue)}
                  slotProps={{ textField: { size: 'small', fullWidth: true } }}
                />
              </Grid>
              <Grid item xs={12} sm={2}>
                <Button fullWidth variant="text" onClick={limparFiltros}>Limpar</Button>
              </Grid>
            </Grid>
            <List>
              {documentosFiltrados.map((docItem) => (
                <ListItem key={docItem.id} divider secondaryAction={
                  isAdmin && (
                    <IconButton edge="end" aria-label="delete" onClick={() => handleDeleteDocumento(docItem)}>
                      <DeleteIcon />
                    </IconButton>
                  )
                }>
                  <ListItemText
                    primary={<MuiLink href={docItem.url_pdf} target="_blank" rel="noopener noreferrer">{docItem.nome}</MuiLink>}
                    secondary={`Categoria: ${docItem.categoria} | Vencimento: ${formatarData(docItem.data_vencimento)} ${docItem.estoque_nome ? `| Ref: ${docItem.estoque_nome}` : ''} ${isAdmin ? `| ${docItem.publico ? 'Público' : 'Privado'}` : ''}`}
                  />
                </ListItem>
              ))}
            </List>
          </CardContent>
        </Card>
      </Box>
    </Box>
  );
};

export default Documentos;